/**
 * AdminContracts — Round 2 · ICMA agreement management.
 *
 * /admin/settings/contracts
 *
 * Admin sees the full version list (draft/active/superseded/withdrawn),
 * acceptance counts, and can (a) view any version body verbatim, (b)
 * publish a fresh version, and (c) withdraw a version.
 *
 * Round 2 constraint: `publish` requires the operator to paste the exact
 * body (markdown + html) so no accidental client-side rendering divergence
 * can slip into the server-stored body_hash. Contract source is
 * /app/memory/CONTRACT_ICMA_ROUND1_DRAFT_v3.md.
 *
 * This UI ships without publishing — the current agreement remains in
 * DRAFT until the owner explicitly clicks Publish and confirms.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, FileText, Eye, Trash2, Rocket } from 'lucide-react';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';

function fmt(dt) { return dt ? new Date(dt).toLocaleString() : '—'; }

export default function AdminContracts() {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingVersion, setViewingVersion] = useState(null);
  const [viewingBody, setViewingBody] = useState('');
  const [showPublish, setShowPublish] = useState(false);
  const [publishForm, setPublishForm] = useState({
    agreement_version: '', title: 'iEvntür Independent Contractor and Marketplace Agreement',
    body_markdown: '', body_html: '', effective_date: new Date().toISOString().slice(0, 10),
    supersedes_version: '',
  });
  const [publishing, setPublishing] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/icma/versions');
      setVersions(res.data);
    } catch (e) {
      toast.error('Failed to load agreement versions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const viewBody = async (agreement_version) => {
    try {
      // The public /icma/current endpoint returns body_html of the active
      // version; for others there's no getter yet in Round 2 — this is
      // documented in Part F. Fall back to a note for non-active rows.
      const active = versions.find((v) => v.agreement_version === agreement_version && v.status === 'active');
      if (active) {
        const res = await api.get('/icma/current');
        setViewingVersion(agreement_version);
        setViewingBody(res.data.body_html);
      } else {
        setViewingVersion(agreement_version);
        setViewingBody(
          '<em>Non-active versions can only be inspected through the database in Round 2. Add a per-version GET endpoint if this is required for review UX.</em>'
        );
      }
    } catch (e) {
      toast.error('Failed to load body');
    }
  };

  const doPublish = async () => {
    if (!window.confirm(
      `Publish ${publishForm.agreement_version}? This will:\n\n` +
      `  1. Insert this as the ACTIVE agreement\n` +
      `  2. Mark the previous active agreement as SUPERSEDED\n` +
      `  3. Set requires_agreement_reaccept=TRUE on every ACTIVE talent account\n` +
      `  4. Body will become IMMUTABLE — you cannot edit it after publish\n\n` +
      `This CANNOT be undone via the UI (only via a new publish that supersedes it).`
    )) return;
    setPublishing(true);
    try {
      await api.post('/admin/icma/publish', publishForm);
      toast.success('Agreement published');
      setShowPublish(false);
      setPublishForm({ ...publishForm, agreement_version: '', body_markdown: '', body_html: '' });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  const withdraw = async (agreement_version) => {
    const reason = window.prompt(`Reason for withdrawing ${agreement_version}?`);
    if (!reason) return;
    try {
      await api.post('/admin/icma/withdraw', { agreement_version, reason });
      toast.success('Withdrawn');
      refresh();
    } catch (e) {
      toast.error('Withdraw failed');
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="admin-contracts-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileText className="w-6 h-6" />
          <h1 className="text-2xl font-bold">iEvntür Contractor Agreement (ICMA)</h1>
        </div>
        <Button onClick={() => setShowPublish(!showPublish)} data-testid="admin-toggle-publish-btn">
          <Rocket className="w-4 h-4 mr-2" /> {showPublish ? 'Cancel' : 'Publish new version'}
        </Button>
      </div>

      {showPublish && (
        <Card className="mb-6 border-2 border-amber-300">
          <CardHeader>
            <CardTitle className="text-base">Publish new version</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="text-xs font-semibold">Version</label>
              <Input
                placeholder="iev-icma-2026.10.01"
                value={publishForm.agreement_version}
                onChange={(e) => setPublishForm({ ...publishForm, agreement_version: e.target.value })}
                data-testid="admin-publish-version"
              />
            </div>
            <div>
              <label className="text-xs font-semibold">Title</label>
              <Input
                value={publishForm.title}
                onChange={(e) => setPublishForm({ ...publishForm, title: e.target.value })}
                data-testid="admin-publish-title"
              />
            </div>
            <div>
              <label className="text-xs font-semibold">Effective date (YYYY-MM-DD)</label>
              <Input
                value={publishForm.effective_date}
                onChange={(e) => setPublishForm({ ...publishForm, effective_date: e.target.value })}
                data-testid="admin-publish-effective"
              />
            </div>
            <div>
              <label className="text-xs font-semibold">Body markdown (from /app/memory/CONTRACT_ICMA_ROUND1_DRAFT_v3.md)</label>
              <textarea
                className="w-full h-40 border rounded p-2 font-mono text-xs"
                value={publishForm.body_markdown}
                onChange={(e) => setPublishForm({ ...publishForm, body_markdown: e.target.value })}
                data-testid="admin-publish-markdown"
              />
            </div>
            <div>
              <label className="text-xs font-semibold">Body HTML (rendered from the markdown above)</label>
              <textarea
                className="w-full h-40 border rounded p-2 font-mono text-xs"
                value={publishForm.body_html}
                onChange={(e) => setPublishForm({ ...publishForm, body_html: e.target.value })}
                data-testid="admin-publish-html"
              />
            </div>
            <Button
              onClick={doPublish}
              disabled={publishing || !publishForm.agreement_version || !publishForm.body_html}
              className="bg-amber-700 hover:bg-amber-800 text-white"
              data-testid="admin-publish-btn"
            >
              {publishing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Publish (irreversible)
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Versions ({loading ? '…' : versions.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm" data-testid="admin-versions-table">
            <thead>
              <tr className="text-left border-b">
                <th className="py-2">Version</th>
                <th>Status</th>
                <th>Effective</th>
                <th>Published</th>
                <th>Superseded</th>
                <th>Acceptances</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.id} className="border-b" data-testid={`admin-version-row-${v.agreement_version}`}>
                  <td className="py-2 font-mono text-xs">{v.agreement_version}</td>
                  <td>
                    <span className={`inline-block px-2 py-0.5 rounded text-xs ${
                      v.status === 'active' ? 'bg-emerald-100 text-emerald-800' :
                      v.status === 'superseded' ? 'bg-slate-200 text-slate-800' :
                      v.status === 'withdrawn' ? 'bg-rose-100 text-rose-800' :
                      'bg-amber-100 text-amber-800'
                    }`}>
                      {v.status}
                    </span>
                  </td>
                  <td>{v.effective_date}</td>
                  <td>{fmt(v.published_at)}</td>
                  <td>{fmt(v.superseded_at)}</td>
                  <td>{v.acceptance_count}</td>
                  <td className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => viewBody(v.agreement_version)}
                            data-testid={`admin-view-body-${v.agreement_version}`}>
                      <Eye className="w-3 h-3" />
                    </Button>
                    {v.status === 'active' && (
                      <Button size="sm" variant="ghost" onClick={() => withdraw(v.agreement_version)}
                              data-testid={`admin-withdraw-${v.agreement_version}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {!loading && versions.length === 0 && (
                <tr><td colSpan={7} className="py-4 text-center text-slate-500">
                  No agreement versions exist yet. Click <b>Publish new version</b> to create the first ICMA.
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {viewingVersion && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-sm">Body: <span className="font-mono">{viewingVersion}</span></CardTitle>
          </CardHeader>
          <CardContent
            className="prose max-w-none max-h-[50vh] overflow-y-auto text-xs"
            dangerouslySetInnerHTML={{ __html: viewingBody }}
            data-testid="admin-view-body-panel"
          />
        </Card>
      )}
    </div>
  );
}
